import { useEffect, useState } from "react";

import { FormProvider, useForm } from "react-hook-form";

import Box from "@/components/Box";
import AddressFinderSearch from "@/components/shared/AddressFinderSearch";
import { FadeTransition } from "@/components/animations/FadeTransition";
import PartnerDashboardBox from "@/components/boxes/PartnerDashboardBox";
import { useOrganisationBasketContext } from "@/contexts/organisations/OrganisationBasketContext";
import { useOrganisationContext } from "@/contexts/organisations/OrganisationContext";
import { BASKET_PAYEE_PATIENT } from "@/core/constants";
import useShippingAddressAndFinalForm from "@/hooks/organisations/useShippingAddressAndFinalForm";
import { useShopPermissions } from "@/hooks/useShopPermissions";
import Center from "@/tpo/Center";
import ChevronComponent from "@/tpo/Chevron";
import ControlledFormField from "@/tpo/ControlledFormField";
import FloatingLabelInput from "@/tpo/FloatingLabelInput";
import Group from "@/tpo/Group";
import NativeSelect from "@/tpo/NativeSelect";
import Stack from "@/tpo/Stack";
import ToggleSwitch from "@/tpo/ToggleSwitch";
import ButtonV2, { IndicativeButton } from "@/v2/Buttons";
import { getShippingAddressFieldNames } from "@/utils/idealPostcodesAddress";

import { canSubmitCheckoutWithBasketPatients } from "../../shared/PatientDetails";
import PatientSelection from "../ui/PatientSelection";
import DnaPreTestCounsellingAcknowledgement from "../ui/DnaPreTestCounsellingAcknowledgement";

const SHIPPING_ADDRESS_PREFIX = "shippingAddress";

function ShippingAddressDetailFields({ prefix, editable, loading, shippingCountries, onAddressPopulated }) {
  const fieldNames = getShippingAddressFieldNames({ prefix, style: "snake" });

  return (
    <>
      {editable && !loading && (
        <AddressFinderSearch
          disabled={!editable || loading}
          shippingCountries={shippingCountries}
          fieldNames={fieldNames}
          countryValueType="isoCode"
          onAddressPopulated={onAddressPopulated}
        />
      )}
      <ControlledFormField
        name={`${prefix}.name`}
        Component={FloatingLabelInput}
        label="Name"
        editable={editable}
      />
      <ControlledFormField
        name={`${prefix}.line_1`}
        Component={FloatingLabelInput}
        label="Address line 1"
        editable={editable}
      />
      <ControlledFormField
        name={`${prefix}.line_2`}
        Component={FloatingLabelInput}
        label="Address line 2"
        editable={editable}
      />
      <ControlledFormField
        name={`${prefix}.city`}
        Component={FloatingLabelInput}
        label="City"
        editable={editable}
      />
      <ControlledFormField
        name={`${prefix}.postcode`}
        Component={FloatingLabelInput}
        label="Postcode"
        editable={editable}
      />
      <ControlledFormField
        name={`${prefix}.phone_number`}
        Component={FloatingLabelInput}
        label="Phone number"
        editable={editable}
      />
    </>
  );
}

function ShippingCountryField({ prefix, editable, loading, shippingCountries }) {
  return (
    <ControlledFormField
      name={`${prefix}.country`}
      rules={{ required: "Please select a country" }}
      Component={NativeSelect}
      label=""
      readOnly={!editable || loading}
    >
      <option value="">Select country</option>
      {shippingCountries.map((country, idx) => (
        <option key={`${country.isoCode}-${idx}`} value={country.isoCode}>
          {country.name}
        </option>
      ))}
    </ControlledFormField>
  );
}

function ShippingAddressAndFinalForm() {
  const { organisation } = useOrganisationContext();
  const { basket, updateBasketDetails, submitMutation, mutation, submissionError, loading } =
    useOrganisationBasketContext();
  const { canPlaceOrders } = useShopPermissions();

  const [showPatientAddressForm, setShowPatientAddressForm] = useState(false);

  const _showPatientAddressForm = basket
    ? basket.patientShippingAddress?.id === basket?.shippingAddress?.id
    : undefined;

  useEffect(() => {
    if (_showPatientAddressForm === undefined) {
      return;
    }
    setShowPatientAddressForm(_showPatientAddressForm);
  }, [_showPatientAddressForm]);

  const submissionErrorMessage = submissionError?.message;

  const formApi = useForm({
    defaultValues: {
      [SHIPPING_ADDRESS_PREFIX]: {
        id: basket?.shippingAddress?.id || "",
        name: basket?.shippingAddress?.name || "",
        line_1: basket?.shippingAddress?.line1 || "",
        line_2: basket?.shippingAddress?.line2 || "",
        city: basket?.shippingAddress?.city || "",
        postcode: basket?.shippingAddress?.postcode || "",
        country: basket?.shippingAddress?.country?.isoCode || "",
        phone_number: basket?.shippingAddress?.phoneNumber || ""
      },
      organisation: organisation?.id || "",
      basket: basket?.id || "",
      // these form values are constants
      addressForPatient: true,
      addressForOrganisation: false,
      addressForOrganisationPrimaryAddress: false,
      acknowledge: false
    }
  });

  const {
    haveExistingShippingAddress,
    editShippingAddress,
    setEditShippingAddress,
    showButtonsForShippingAddressForm,
    shippingAddressFormEditable,
    shippingAddressSubmitButtonRef,
    onSubmit,
    onInvalid,
    onShippingAddressSubmit,
    saveShippingAddressAtCheckout,
    shippingCountries,
    nonFieldError,
    loading: shippingAddressLoading
  } = useShippingAddressAndFinalForm({
    formApi,
    mutation,
    submitMutation
  });

  useEffect(() => {
    if (basket?.id && organisation?.id) {
      formApi.reset({
        [SHIPPING_ADDRESS_PREFIX]: {
          id: basket?.shippingAddress?.id || "",
          name: basket?.shippingAddress?.name || "",
          line_1: basket?.shippingAddress?.line1 || "",
          line_2: basket?.shippingAddress?.line2 || "",
          city: basket?.shippingAddress?.city || "",
          postcode: basket?.shippingAddress?.postcode || "",
          country: basket?.shippingAddress?.country?.isoCode || "",
          phone_number: basket?.shippingAddress?.phoneNumber || ""
        },
        organisation: organisation?.id || "",
        basket: basket?.id || "",
        addressForPatient: true,
        addressForOrganisation: false,
        addressForOrganisationPrimaryAddress: false,
        acknowledge: formApi.getValues("acknowledge") ?? false
      });
    }
  }, [
    formApi,
    basket?.id,
    basket?.shippingAddress?.id,
    basket?.shippingAddress?.name,
    basket?.shippingAddress?.line1,
    basket?.shippingAddress?.line2,
    basket?.shippingAddress?.city,
    basket?.shippingAddress?.postcode,
    basket?.shippingAddress?.country?.isoCode,
    basket?.shippingAddress?.phoneNumber,
    organisation?.id,
    showPatientAddressForm
  ]);

  const isPatientPay = basket?.payee === BASKET_PAYEE_PATIENT;
  const selectedCountry = formApi.watch(`${SHIPPING_ADDRESS_PREFIX}.country`);
  const addressFieldsEditable = shippingAddressFormEditable && !loading;

  const handleAddressPopulated = () => {
    saveShippingAddressAtCheckout(formApi.getValues());
  };

  const canSubmitBasedOnOrganisationPatient = canSubmitCheckoutWithBasketPatients({
    basket,
    organisationPatients: organisation?.patients || [],
    payee: basket?.payee
  });

  return (
    <FormProvider {...formApi}>
      <Stack
        as="form"
        gap={[20, 20, 40]}
        onSubmit={
          showButtonsForShippingAddressForm
            ? onShippingAddressSubmit
            : formApi.handleSubmit(onSubmit, onInvalid)
        }
        data-component-name="ShippingAddressForm"
      >
        <PartnerDashboardBox>
          {basket?.patientShippingAddress && !basket?.multiplePatientsEnabled && (
            <Group alignItems="center" gap={10}>
              <Box
                fontFamily="gilroyBold"
                fontSize={12}
                textTransform="uppercase"
                letterSpacing="2.8px"
              >
                use patient address
              </Box>
              <ToggleSwitch
                onChange={checked => {
                  setShowPatientAddressForm(checked);
                  updateBasketDetails({
                    usePatientAddress: checked
                  });
                }}
                value={showPatientAddressForm}
                data-testid="use-patient-address-toggle"
                id="use-patient-address-toggle"
                offBg="haze"
                disabled={loading}
              />
            </Group>
          )}
          {isPatientPay ? (
            <Stack gap={16}>
              <Box fontFamily="gilroyBold" fontSize={[24, 24, 28]}>
                Patient shipping address
              </Box>
              <Box fontFamily="gilroyMedium" fontSize={[14, 14, 16]}>
                Only the shipping country is needed now. Your patient will confirm and complete
                their full address at payment.
              </Box>
            </Stack>
          ) : (
            <Stack gap={[24, 24, 28]}>
              <Group alignItems="center" justifyContent="space-between">
                <Box fontFamily="gilroyBold" fontSize={[24, 24, 28]}>
                  {showPatientAddressForm ? "Patient Shipping Address" : "Shipping Address"}
                </Box>
                {haveExistingShippingAddress && (
                  <ButtonV2
                    color="dark"
                    size={["xs", "xs", "sm"]}
                    sx={{
                      color: "white"
                    }}
                    onClick={() => {
                      setEditShippingAddress(!editShippingAddress);
                    }}
                    type="button"
                    disabled={loading}
                  >
                    edit address
                  </ButtonV2>
                )}
              </Group>
            </Stack>
          )}
          <Stack gap={20}>
            {isPatientPay && (
              <Stack gap={0}>
                <Box fontFamily="gilroyBold" fontSize={[14, 14, 16]}>
                  Shipping country
                </Box>
                <Box fontFamily="gilroyMedium" fontSize={[12, 12, 14]}>
                  Used to calculate shipping and can&apos;t be changed after the order is placed.
                </Box>
                <Stack mt={28}>
                  <ShippingCountryField
                    prefix={SHIPPING_ADDRESS_PREFIX}
                    editable={addressFieldsEditable}
                    loading={loading}
                    shippingCountries={shippingCountries}
                  />
                </Stack>
              </Stack>
            )}
            {isPatientPay && (
              <Stack gap={0} mt={20}>
                <Box fontFamily="gilroyBold" fontSize={[14, 14, 16]}>
                  Shipping address (optional)
                </Box>
                <Box fontFamily="gilroyMedium" fontSize={[12, 12, 14]}>
                  Add your patient&apos;s address if you have it. They&apos;ll be asked to review it,
                  or complete it, before payment.
                </Box>
                <Stack gap={20} mt={28}>
                  <ShippingAddressDetailFields
                    prefix={SHIPPING_ADDRESS_PREFIX}
                    editable={addressFieldsEditable}
                    loading={loading}
                    shippingCountries={shippingCountries}
                    onAddressPopulated={handleAddressPopulated}
                  />
                </Stack>
              </Stack>
            )}
            {!isPatientPay && (
              <>
                <ShippingAddressDetailFields
                  prefix={SHIPPING_ADDRESS_PREFIX}
                  editable={addressFieldsEditable}
                  loading={loading}
                  shippingCountries={shippingCountries}
                  onAddressPopulated={handleAddressPopulated}
                />
                <ShippingCountryField
                  prefix={SHIPPING_ADDRESS_PREFIX}
                  editable={addressFieldsEditable}
                  loading={loading}
                  shippingCountries={shippingCountries}
                />
              </>
            )}
          </Stack>
          {isPatientPay && haveExistingShippingAddress && !showButtonsForShippingAddressForm && (
            <Group justifyContent="flex-start">
              <ButtonV2
                color="dark"
                size={["xs", "xs", "sm"]}
                sx={{
                  color: "white"
                }}
                onClick={() => {
                  setEditShippingAddress(!editShippingAddress);
                }}
                type="button"
                disabled={loading}
              >
                edit address
              </ButtonV2>
            </Group>
          )}
          {showButtonsForShippingAddressForm ? (
            <Center gap={20}>
              <ButtonV2
                type="button"
                color="red"
                rightIcon={<ChevronComponent />}
                onClick={() => {
                  formApi.reset();
                  setEditShippingAddress(false);
                }}
                size={["sm", "sm", "md"]}
              >
                Cancel
              </ButtonV2>
              <IndicativeButton
                rightIcon={<ChevronComponent />}
                ref={shippingAddressSubmitButtonRef}
                defaultColor="green"
                pendingColor="#0cf970"
                successColor="#2ecc71"
                failureColor="error"
                size={["sm", "sm", "md"]}
                disabled={loading || shippingAddressLoading}
              >
                save
              </IndicativeButton>
            </Center>
          ) : null}
        </PartnerDashboardBox>
        <DnaPreTestCounsellingAcknowledgement />
        {mutation.renderFields({
          nonFieldError,
          error: submissionErrorMessage,
          canSubmit:
            canPlaceOrders &&
            !shippingAddressLoading &&
            !(haveExistingShippingAddress && editShippingAddress) &&
            (!isPatientPay || !!selectedCountry) &&
            (basket?.multiplePatientsEnabled || basket?.patient) &&
            (basket?.productLineItems.length > 0 || basket?.supplementLineItems.length > 0) &&
            canSubmitBasedOnOrganisationPatient
        })}
      </Stack>
    </FormProvider>
  );
}

export default function NonStockCheckoutForm() {
  const { basket } = useOrganisationBasketContext();

  return (
    <>
      <FadeTransition in={true}>
        {!basket?.multiplePatientsEnabled && <PatientSelection />}
      </FadeTransition>
      <ShippingAddressAndFinalForm />
    </>
  );
}
